import * as React from "react";

import { AGENT_THREAD_TIME_FORMAT_STORAGE_KEY } from "./storageKey";
import { TimestampFormatProvider } from "./TimestampFormatContext";
import { useVisualizerClock } from "./useVisualizerClock";

interface ThreadTimestampFormatProviderProps {
  children: React.ReactNode;
  /**
   * Thread whose anchor is in play. Absent while a brand-new thread has not
   * been created yet, which the provider handles by holding a picked anchor
   * until an id arrives.
   */
  threadId?: string;
}

/**
 * The timestamp-format provider as every AI thread surface mounts it: one
 * shared storage key, the thread on screen supplying the per-thread anchor.
 *
 * All three surfaces — the chat sidebar, the standalone thread page, and the
 * admin thread viewer — go through here so the format stays a single global
 * user preference: a choice made in one place carries over to the others, and
 * no surface can drift onto a key of its own.
 *
 * Every surface also wires up the open visualizer's clock, which is what makes
 * "Follow the visualizer" offerable and keeps it in step with the workspace's
 * bottom-left display. On a surface with no workspace open the hook reports
 * nothing, and the mode simply isn't on the menu.
 */
export function ThreadTimestampFormatProvider({
  threadId,
  children,
}: ThreadTimestampFormatProviderProps) {
  const visualizerClock = useVisualizerClock();
  return (
    <TimestampFormatProvider
      storageKey={AGENT_THREAD_TIME_FORMAT_STORAGE_KEY}
      threadId={threadId}
      visualizerClock={visualizerClock}
    >
      {children}
    </TimestampFormatProvider>
  );
}
